import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Polygon, MultiPolygon, Geometry } from 'ol/geom';
import { Fill, Stroke, Style} from 'ol/style';
import { Feature } from 'ol';
import { ZoneFeature } from '../models/zones-base-interfaces';
import circle from '@turf/circle';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface ZoneColors
{
    borderColor: string;
    fillColor: string;
    corridorColor?: string;
}

export abstract class ZoneLayerBase extends EventTarget
{
    abstract getZones(): Promise<ZoneFeature[]>;
    abstract getText(feature: ZoneFeature): string;
    abstract zoneId: string;
    
    private _layer : VectorLayer<VectorSource<Geometry>> | null = null;

    constructor(colors?: Partial<ZoneColors>)
    {
        super();
        this.setColors(colors || {});
    }

    get colors(): ZoneColors
    {
        return this._colors;
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        this._vectorSource = new VectorSource();
        this._layer = new VectorLayer({source: this._vectorSource, visible: false});
        this._layers.push(this._layer);
    }

    setColors(colors: Partial<ZoneColors>): void
    {
        this._colors.borderColor = colors.borderColor || this.colors.borderColor;
        this._colors.fillColor = colors.fillColor || this.colors.fillColor;

        if (this._vectorSource){
            this._clear();
            this.plot();
        }
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    enable() {
        this._clear();
        this.plot().then(() => this._layer!.setVisible(true));
    }

    disable() {
        this._layer!.setVisible(false);
        this._clear();
    }

    private _colors: ZoneColors = {
        borderColor: "black",
        fillColor: "black",
        corridorColor: "blue"
    };

    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _vectorSource: VectorSource<Geometry>|null = null;

    public async plot() {
        this._clear();

        const zoneFeatures = await this.getZones();
        if (!zoneFeatures) {
            return;
        }

        const features: Feature<Geometry>[] = [];
        const fillColor = this.colors.fillColor || "black";
        const borderColor = this.colors.borderColor || "black";
        const corridorColor = this.colors.corridorColor || "blue";

        const zoneStyle = new Style({
            stroke: new Stroke({
                color: borderColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        const corridorStyle = new Style({
            stroke: new Stroke({
                color: corridorColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        zoneFeatures!.forEach(z => {
            let feature: Feature<Geometry>;
            if (z.geometry!.type === "Polygon") {
                feature = new Feature({geometry: new Polygon(<number[][][]>z.geometry!.coordinates)});
            }
            if (z.geometry!.type === "MultiPolygon") {
                feature = new Feature({geometry: new MultiPolygon(<number[][][][]>z.geometry!.coordinates)});
            }
            if (z.geometry!.type === "Circle") {
                const coordinates = circle(<number[]>z.geometry?.coordinates, <number>z.geometry?.radius, {steps: 500, units: 'kilometers'}).geometry.coordinates;
                feature = new Feature({geometry: new Polygon(<number[][][]>coordinates)});
            }
            
            feature!.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            feature!.setStyle(zoneStyle);

            if (z.properties.specialType && z.properties.specialType === "corridor") {
                feature!.setStyle(corridorStyle);
            }

            const text = `${this.getText(z)}`;
            feature!.set('text', text);
            feature!.set('layer', this.zoneId);
            features.push(feature!);
        })      

        this._vectorSource!.addFeatures(features);
    }
};
